import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { SnackbarProvider, enqueueSnackbar, OptionsObject } from "notistack";

import { useAppDispatch } from "../../../../store";
import { AlertPopup } from "../../components/alert-popup/alert-popup";
import { AlertWrapper } from "../../components/alert-wrapper/alert-wrapper";
import { ConfirmDialog } from "../../components/confirm-dialog/confirm-dialog";
import { DEFAULT_NOTIFICATION_TIMEOUT } from "../../shared/constants/notification-timeout";
import { NotificationSeverity } from "../../shared/enums/notification-severity";
import {
  getAlertNotifications,
  getConfirmNotification,
  getSnackNotifications,
} from "../../store/notifications.selector";
import { removeNotificationAction } from "../../store/notifications.slice";

export const NotificationsArea = () => {
  const dispatch = useAppDispatch();
  const confirm = useSelector(getConfirmNotification);
  const alerts = useSelector(getAlertNotifications);
  const snacks = useSelector(getSnackNotifications);
  const alertTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  // snacks handling we forward to notistack library
  useEffect(() => {
    snacks.forEach((snack) => {
      const options: OptionsObject<NotificationSeverity> = {
        variant: snack.severity,
        style: {
          whiteSpace: "pre-wrap",
        },
      };
      enqueueSnackbar(snack.message, options);
      dispatch(removeNotificationAction(snack));
    });
  }, [snacks, dispatch]);

  /**
   * Таймер заводится один раз на уведомление и живёт по его id: соседнее
   * уведомление меняет список, а срок показа уже висящих не сдвигает.
   */
  useEffect(() => {
    const timers = alertTimers.current;

    for (const alert of alerts) {
      if (timers.has(alert.id)) {
        continue;
      }

      timers.set(
        alert.id,
        setTimeout(
          () => dispatch(removeNotificationAction(alert)),
          alert.timeout ?? DEFAULT_NOTIFICATION_TIMEOUT
        )
      );
    }

    const shownIds = new Set(alerts.map((alert) => alert.id));
    for (const [alertId, timer] of timers) {
      if (!shownIds.has(alertId)) {
        clearTimeout(timer);
        timers.delete(alertId);
      }
    }
  }, [alerts, dispatch]);

  useEffect(() => {
    const timers = alertTimers.current;
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <>
      {/* notistack initialization */}
      <SnackbarProvider maxSnack={3} />

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          onSuccess={confirm.cb}
          severity={confirm.severity}
          onClose={() => dispatch(removeNotificationAction(confirm))}
          confirmOnly={confirm.confirmOnly}
          confirmLabel={confirm.confirmLabel}
          isResultDialog={confirm.isResultDialog}
        />
      )}

      <AlertWrapper>
        {alerts.map((item, index) => (
          <AlertPopup
            key={`alert-popup-${index}`}
            title={item.title}
            message={item.message}
            severity={item.severity}
            onClose={() => dispatch(removeNotificationAction(item))}
          />
        ))}
      </AlertWrapper>
    </>
  );
};
